import type { ReactElement, ReactNode } from 'react';
import { Children, useCallback, useEffect, useRef, useState } from 'react';
import { useYieldCallback } from '@zillow/yield-callback';
import ArrowButton from './ArrowButton';
import PaginationDots from './PaginationDots';
import VisuallyHidden from 'app/shared/modules/VisuallyHidden';
import {
  CarouselContainer,
  CarouselContentWrapper,
  CarouselHeader,
  CarouselItem,
  CarouselItemsContainer,
  CarouselSlider,
  CarouselTitle,
  SeeAllButtonWrapper,
  SkipButton,
} from './CarouselV3.styles';

type HeadingTag = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

interface CarouselV3Props {
  'aria-label'?: string;
  children: ReactNode;
  className?: string;
  freeScrollMode?: boolean;
  header?: string | ReactNode;
  headerTag?: HeadingTag;
  isHdpFeaturedListings?: boolean;
  itemsPerPage: number;
  seeAllButton?: ReactNode;
  showPaginationDots?: boolean;
  showScrollbar?: boolean;
  style?: React.CSSProperties;
}

const getFocusableElementInItem = (item: HTMLElement): HTMLElement | null => {
  const focusableSelectors = 'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])';
  return item.querySelector(focusableSelectors);
};

const isFocusFromKeyboard = (): boolean => {
  // Check if there's a recent keyboard event that focuses an element
  const activeElement = document.activeElement;
  if (!activeElement) {
    return false;
  }

  // If the element has :focus-visible, it was focused with keyboard
  try {
    return activeElement.matches(':focus-visible');
  } catch {
    return false;
  }
};

/**
 * CarouselV3. Supports pagination-based scrolling (desktop) or free scrolling (mobile).
 * @param {ReactNode} children - The carousel items. It is expected to be a list of items that are the same width.
 * @param {boolean} freeScrollMode - Enable free scroll mode. Enable for mobile devices, arrows/pagination disabled.
 * @param {number} itemsPerPage - Number of items per page.
 * @param {boolean} showPaginationDots - Flag for showing pagination dots below the carousel.
 * @param {boolean} showScrollbar - Show horizontal scrollbar in free scroll mode. Defaults to false (hidden).
 * @param {string | ReactNode} header - Optional header to display above the carousel.
 * @param {HeadingTag} headerTag - HTML tag to use for the header. Defaults to 'h2'.
 * @param {boolean} isHdpFeaturedListings - Carousel is used in the HDP featured listing collections.
 * @param {ReactNode} seeAllButton - Optional button to render on the right of the arrow buttons.
 * @param {React.CSSProperties} style - Optional inline styles to apply to the carousel container.
 * @returns {ReactNode} The carousel component
 */
export const CarouselV3 = ({
  'aria-label': ariaLabel,
  children,
  className,
  freeScrollMode = false,
  header,
  headerTag = 'h2',
  isHdpFeaturedListings = false,
  itemsPerPage,
  seeAllButton,
  showPaginationDots = true,
  showScrollbar = false,
  style = {},
}: CarouselV3Props): ReactNode => {
  const [currentPage, setCurrentPage] = useState(0);
  const [itemWidth, setItemWidth] = useState(0);
  const carouselContainerRef = useRef<HTMLDivElement | null>(null);
  const carouselSliderRef = useRef<HTMLUListElement | null>(null);
  const freeScrollModeContainerRef = useRef<HTMLDivElement | null>(null);
  const liveRegionRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef<number>(0);
  const touchStartY = useRef<number>(0);
  const touchStartTime = useRef<number>(0);

  const itemArray = Children.toArray(children);
  const totalItems = itemArray.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const isAtStart = currentPage === 0;
  const isAtEnd = currentPage >= totalPages - 1;
  const itemsGap = 16; // Gap of 16px between each item b.c of CarouselSlider style.
  const translateXPixels = -(currentPage * itemsPerPage * (itemWidth + itemsGap));

  const slideCarousel = useCallback(
    (direction: 'left' | 'right') => {
      setCurrentPage((prev) => {
        const newPage = direction === 'left' ? Math.max(0, prev - 1) : Math.min(totalPages - 1, prev + 1);

        if (liveRegionRef.current && newPage !== prev) {
          liveRegionRef.current.textContent = `Page ${newPage + 1} of ${totalPages}`;
        }

        return newPage;
      });
    },
    [totalPages],
  );

  const yieldSlideCarousel = useYieldCallback((direction: 'left' | 'right') => {
    slideCarousel(direction);
  });

  const handleSkipToBeginning = useCallback(() => {
    if (freeScrollMode) {
      // In free scroll mode, scroll the container to the beginning
      freeScrollModeContainerRef.current?.scrollTo({ left: 0, behavior: 'smooth' });
    } else {
      setCurrentPage(0);
    }

    setTimeout(() => {
      const carouselSlider = carouselSliderRef.current;
      if (!carouselSlider) {
        return;
      }

      const items = Array.from(carouselSlider.children) as Array<HTMLElement>;
      const targetItem = items[0];

      if (targetItem) {
        const focusableElement = getFocusableElementInItem(targetItem);
        if (focusableElement) {
          focusableElement.focus();
        }
      }

      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = freeScrollMode
          ? 'Skipped to beginning of carousel'
          : `Skipped to beginning. Page 1 of ${totalPages}`;
      }
    }, 0);
  }, [freeScrollMode, totalPages]);

  const handleSkipToEnd = useCallback(() => {
    if (freeScrollMode) {
      const freeScrollModeContainer = freeScrollModeContainerRef.current;
      if (freeScrollModeContainer) {
        freeScrollModeContainer.scrollTo({ left: freeScrollModeContainer.scrollWidth, behavior: 'smooth' });
      }
    } else {
      const lastPage = totalPages - 1;
      setCurrentPage(lastPage);
    }

    setTimeout(() => {
      const carouselSlider = carouselSliderRef.current;
      if (!carouselSlider) {
        return;
      }

      const items = Array.from(carouselSlider.children) as Array<HTMLElement>;
      const lastItemIndex = items.length - 1;
      const targetItem = items[lastItemIndex];

      if (targetItem) {
        const focusableElement = getFocusableElementInItem(targetItem);
        if (focusableElement) {
          focusableElement.focus();
        }
      }

      if (liveRegionRef.current) {
        liveRegionRef.current.textContent = freeScrollMode
          ? 'Skipped to end of carousel'
          : `Skipped to end. Page ${totalPages} of ${totalPages}`;
      }
    }, 0);
  }, [freeScrollMode, totalPages]);

  const yieldHandleSkipToBeginning = useYieldCallback(() => {
    handleSkipToBeginning();
  });

  const yieldHandleSkipToEnd = useYieldCallback(() => {
    handleSkipToEnd();
  });

  // Get the width of the first carousel item to calculate carousel slide translations
  useEffect(() => {
    const carouselSlider = carouselSliderRef.current;
    if (!carouselSlider) return;

    const firstItem = carouselSlider.firstElementChild as HTMLElement;
    if (firstItem) {
      const width = firstItem.offsetWidth;
      setItemWidth(width);
    }
  }, [children]);

  // Handle swipe gestures for paginated mode, not free scroll
  useEffect(() => {
    if (freeScrollMode) return;

    const carouselSlider = carouselSliderRef.current;
    if (!carouselSlider) return;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX;
      touchStartY.current = e.touches[0].clientY;
      touchStartTime.current = Date.now();
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (touchStartX.current === 0) return;

      const currentTouchX = e.touches[0].clientX;
      const currentTouchY = e.touches[0].clientY;
      const deltaTouchX = Math.abs(currentTouchX - touchStartX.current);
      const deltaTouchY = Math.abs(currentTouchY - touchStartY.current);

      // Allow vertical scrolling and prevent default scroll behavior if swipe is more horizontal
      if (deltaTouchX > deltaTouchY) {
        e.preventDefault();
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndX = e.changedTouches[0].clientX;
      const touchDuration = Date.now() - touchStartTime.current;
      const touchDistance = touchStartX.current - touchEndX;

      const minSwipeDistance = 50;
      const maxSwipeDuration = 500;

      if (Math.abs(touchDistance) > minSwipeDistance && touchDuration < maxSwipeDuration) {
        if (touchDistance > 0) {
          yieldSlideCarousel('right');
        } else {
          yieldSlideCarousel('left');
        }
      }

      touchStartX.current = 0;
      touchStartY.current = 0;
      touchStartTime.current = 0;
    };

    carouselSlider.addEventListener('touchstart', handleTouchStart, { passive: true });
    carouselSlider.addEventListener('touchmove', handleTouchMove, { passive: false });
    carouselSlider.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      if (freeScrollMode) return;

      carouselSlider.removeEventListener('touchstart', handleTouchStart);
      carouselSlider.removeEventListener('touchmove', handleTouchMove);
      carouselSlider.removeEventListener('touchend', handleTouchEnd);
    };
  }, [freeScrollMode, yieldSlideCarousel]);

  // Handle left/right arrow keys
  useEffect(() => {
    const carouselContainer = carouselContainerRef.current;
    if (!carouselContainer) return;

    const focusFirstItemOnPage = (pageIndex: number) => {
      setTimeout(() => {
        const carouselSlider = carouselSliderRef.current;
        if (!carouselSlider) return;

        const items = Array.from(carouselSlider.children) as Array<HTMLElement>;
        const firstItemIndex = pageIndex * itemsPerPage;
        const targetItem = items[firstItemIndex];

        if (targetItem) {
          const focusableElement = getFocusableElementInItem(targetItem);
          if (focusableElement) {
            focusableElement.focus();
          }
        }
      }, 0);
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (!carouselContainer.contains(document.activeElement)) {
        return;
      }

      if (e.key === 'ArrowLeft' && !isAtStart) {
        e.preventDefault();
        yieldSlideCarousel('left');
        focusFirstItemOnPage(currentPage - 1);
      } else if (e.key === 'ArrowRight' && !isAtEnd) {
        e.preventDefault();
        yieldSlideCarousel('right');
        focusFirstItemOnPage(currentPage + 1);
      }
    };

    carouselContainer.addEventListener('keydown', handleKeyDown);

    return () => {
      carouselContainer.removeEventListener('keydown', handleKeyDown);
    };
  }, [yieldSlideCarousel, isAtStart, isAtEnd, currentPage, itemsPerPage]);

  // Update page and pagination dots when focusing items - mainly for keyboard navigation.
  useEffect(() => {
    const carouselSlider = carouselSliderRef.current;
    const carouselContainer = carouselContainerRef.current;
    const freeScrollModeContainer = freeScrollModeContainerRef.current;
    if (!carouselSlider) return;

    const handleFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement;

      const carouselItems = Array.from(carouselSlider.children) as Array<HTMLElement>;
      const focusedItemIndex = carouselItems.findIndex((item) => item.contains(target));

      if (focusedItemIndex !== -1) {
        const focusedItem = carouselItems[focusedItemIndex];

        if (freeScrollMode && freeScrollModeContainer && focusedItem) {
          if (isFocusFromKeyboard()) {
            focusedItem.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        } else {
          const pageWithFocusedItem = Math.floor(focusedItemIndex / itemsPerPage);

          if (pageWithFocusedItem !== currentPage && isFocusFromKeyboard()) {
            setCurrentPage(pageWithFocusedItem);

            if (liveRegionRef.current) {
              liveRegionRef.current.textContent = `Page ${pageWithFocusedItem + 1} of ${totalPages}`;
            }

            if (carouselContainer) {
              carouselContainer.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }
        }
      }
    };

    carouselSlider.addEventListener('focusin', handleFocusIn);

    return () => {
      carouselSlider.removeEventListener('focusin', handleFocusIn);
    };
  }, [currentPage, freeScrollMode, itemsPerPage, totalPages]);

  return (
    <CarouselContainer className={className} style={style} ref={carouselContainerRef}>
      <VisuallyHidden>
        <div aria-live="polite" aria-atomic="true" ref={liveRegionRef} />
      </VisuallyHidden>
      <CarouselHeader>
        {header ? (
          typeof header === 'string' ? (
            <CarouselTitle as={headerTag} isHdpFeaturedListings={isHdpFeaturedListings}>
              {header}
            </CarouselTitle>
          ) : (
            header
          )
        ) : (
          <div />
        )}
        {seeAllButton && <SeeAllButtonWrapper>{seeAllButton}</SeeAllButtonWrapper>}
      </CarouselHeader>
      {totalPages > 1 && (
        <SkipButton position="top" onClick={yieldHandleSkipToEnd} type="button">
          Skip to end of carousel
        </SkipButton>
      )}
      <CarouselContentWrapper>
        {!freeScrollMode && (
          <ArrowButton left position="left" onClick={() => yieldSlideCarousel('left')} disabled={isAtStart} />
        )}
        <CarouselItemsContainer
          ref={freeScrollModeContainerRef}
          role="region"
          aria-label={ariaLabel || 'Carousel'}
          aria-roledescription="carousel"
          freeScrollMode={freeScrollMode}
          showScrollbar={showScrollbar}
        >
          <CarouselSlider
            ref={carouselSliderRef}
            style={freeScrollMode ? undefined : { transform: `translateX(${translateXPixels}px)` }}
            freeScrollMode={freeScrollMode}
          >
            {itemArray.map((child, index) => {
              const pageIndex = Math.floor(index / itemsPerPage);
              const isVisible = freeScrollMode || pageIndex === currentPage;
              const key = (child as ReactElement).key ?? `carousel-item-${index}`;
              return (
                <CarouselItem
                  key={key}
                  role="group"
                  aria-roledescription="slide"
                  aria-label={`${index + 1} of ${totalItems}`}
                  aria-hidden={!isVisible}
                >
                  {child}
                </CarouselItem>
              );
            })}
          </CarouselSlider>
        </CarouselItemsContainer>
        {!freeScrollMode && (
          <ArrowButton position="right" onClick={() => yieldSlideCarousel('right')} disabled={isAtEnd} />
        )}
      </CarouselContentWrapper>
      {!freeScrollMode && showPaginationDots && <PaginationDots currentPage={currentPage} totalPages={totalPages} />}
      {totalPages > 1 && (
        <SkipButton position="bottom" onClick={yieldHandleSkipToBeginning} type="button">
          Skip to beginning of carousel
        </SkipButton>
      )}
    </CarouselContainer>
  );
};

export default CarouselV3;
